import { useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { ExternalLink, PackagePlus, Upload } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { SkeletonRows } from '@/components/ui/skeleton'
import { QuoteTimestamps } from '../../../../components/Chat/OfferTimestamps'
import { FailureAnalysisPanel } from '../../../../components/insurance/FailureAnalysisPanel'
import { ADMIN_BASE_URL, adminHref } from '../../../../config/config'
import { CacheKeys } from '../../../../lib/cache'
import { parseError } from '../../../../lib/errors'
import { formatDate } from '../../../../lib/formatters'
import { formatCurrency } from '../../../../lib/utils'
import { AWAITING_CONFIRMATION_REASON, useFillApplicationState } from '../../../../models/fillApplication'
import { useFeatureFlag } from '../../../../models/flag'
import { RocklakeLineState } from '../../../../models/insuranceApplication'
import {
  deleteIndication,
  generateQuoteFromIndication,
  type InsuranceIndication as InsuranceIndicationType,
  type ParsedPolicyIndication,
  setIndicationApplied,
  setIndicationRecommended,
} from '../../../../models/insuranceIndication'
import { InsurancePolicy as InsurancePolicyType, PolicyStatus } from '../../../../models/insurancePolicy'
import { deleteQuote } from '../../../../models/insuranceQuote'
import {
  InsuranceIndicationStatus,
  InsuranceOfferKind,
  InsuranceQuoteStatus,
} from '../../../../models/insuranceTypes'
import { Org, showsQuotesPage, useCurrentUser, useOrgApplication } from '../../../../models/user'
import { CorgiPolicyLinks } from '../Insurance/CorgiPolicyLinks'
import { InsuranceIndications } from '../Insurance/InsuranceIndications'
import { InsurancePolicy } from '../Insurance/InsurancePolicy'
import { CustomerConfirmationWaiting } from './FillApplication/CustomerConfirmationWaiting'
import { BindDialog } from './Insurance/BindDialog'
import { CancelPolicyDialog } from './Insurance/CancelPolicyDialog'
import { CreatePackageDialog } from './Insurance/CreatePackageDialog'
import { EditQuoteDialog } from './Insurance/EditQuoteDialog'
import { IndicationFormDialog, type IndicationPrefill } from './Insurance/IndicationFormDialog'
import { PackageStatus } from './Insurance/PackageStatus'
import { ParsePolicyDialog } from './Insurance/ParsePolicyDialog'
import { PolicyDocumentReviewWatcher } from './Insurance/PolicyDocumentReviewWatcher'
import { PolicyFormDialog, type PolicyPrefill } from './Insurance/PolicyFormDialog'
import { PostBindChecklist } from './Insurance/PostBindChecklist'
import { ProgressiveNotRunCard } from './Insurance/ProgressiveNotRunCard'
import { QuoteDeclineBanner } from './Insurance/QuoteDeclineBanner'
import { RecommendOfferButton } from './Insurance/RecommendOfferButton'
import { RequestToBindButton } from './Insurance/RequestToBindButton'
import { RocklakeLinesCard } from './Insurance/RocklakeLinesCard'
import { UploadPolicyDocumentDialog } from './Insurance/UploadPolicyDocumentDialog'

// Keyed camelCase: these are object *keys* inside manualReviewReasons, which
// humps camelizes. The label map below stays snake_case — it's keyed by
// values, which humps leaves alone.
const COVERAGE_LABELS: Record<string, string> = {
  autoLiabilityPowerUnit: 'Commercial Auto Liability',
  cargoContractualLiability: 'Cargo Contractual Liability',
  generalLiability: 'General Liability',
  physicalDamage: 'Physical Damage',
}

// A policy canceled as a whole, or already over, has nothing left to cancel.
const CANCELABLE_STATUSES = new Set([PolicyStatus.ACTIVE, PolicyStatus.EXPIRING, PolicyStatus.UPCOMING])

const MANUAL_REVIEW_LABELS: Record<string, string> = {
  underwriting_review: 'Held for underwriter review',
  submission_incomplete: 'Submission incomplete — fix and resubmit',
  vehicle_data_unverified: 'Vehicle data could not be verified',
  declared_operations_review: 'Declared operations require review',
  data_verification_review: 'Data disagrees with FMCSA records',
}

export function InsuranceTab({ org }: { org: Org }) {
  const orgId = org.id
  const { application, isLoading } = useOrgApplication(orgId)
  const queryClient = useQueryClient()
  const canBindQuote = useFeatureFlag('ENABLE_BIND_QUOTE')
  const { user } = useCurrentUser({ enabled: false })
  // Staff bind quotes in the internal console regardless of the customer-facing flag.
  const canBind = canBindQuote || !!user?.isCaiAdmin
  const policies = org.insurancePolicies ?? []
  // The server refuses to bind until the customer confirms their information on /quotes.
  const { state: fillState } = useFillApplicationState(orgId)
  const bindBlockedReason = fillState?.needsReviewAttestation
    ? `${AWAITING_CONFIRMATION_REASON} Send them a sign-in link from the Intake tab.`
    : null
  const indications = application?.indications ?? []
  const hasUnsentRocklakeLines = Object.values(application?.rocklakeManualLines ?? {}).some(
    (state) => state !== RocklakeLineState.SENT
  )
  const [creatingKind, setCreatingKind] = useState<InsuranceOfferKind | null>(null)
  const [creatingPackage, setCreatingPackage] = useState(false)
  // Kept separate from `uploadOpen` so the title doesn't flip mid close-animation.
  const [uploadKind, setUploadKind] = useState<InsuranceOfferKind>(InsuranceOfferKind.INDICATION)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [parsedPrefill, setParsedPrefill] = useState<IndicationPrefill | null>(null)
  const [editingIndication, setEditingIndication] = useState<InsuranceIndicationType | null>(null)
  const [editingQuote, setEditingQuote] = useState<{ indicationId: string; quoteId: string } | null>(null)
  const [bindingQuote, setBindingQuote] = useState<{
    indicationId: string
    quoteId: string
    pricedStartDate: string | null
  } | null>(null)
  const [policyDialog, setPolicyDialog] = useState<{
    policy: InsurancePolicyType
    prefill: PolicyPrefill | null
  } | null>(null)
  const [uploadPolicyDoc, setUploadPolicyDoc] = useState<InsurancePolicyType | null>(null)
  const [cancelingPolicy, setCancelingPolicy] = useState<InsurancePolicyType | null>(null)
  // Ids, not policies: the review lands ~30s later, by which time a captured
  // policy would overwrite any edit made in between.
  const [pendingReviews, setPendingReviews] = useState<{ policyId: string; token: string }[]>([])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId] })
    // A policy document upload or removal is what ticks or clears the checklist's policy item.
    queryClient.invalidateQueries({ queryKey: [CacheKeys.POST_BIND_CHECKLIST] })
  }

  const handleDeletePackage = async (packageId: string) => {
    try {
      await axios.delete(`/api/v1/insure/organisations/${orgId}/packages/${packageId}/`)
      invalidate()
      toast.success('Package deleted.')
    } catch (err) {
      toast.error(parseError(err))
    }
  }

  const handleDeletePolicyDoc = async (policy: InsurancePolicyType, attachmentId: string) => {
    try {
      await axios.delete(
        `/api/v1/insure/organisations/${orgId}/policies/${policy.id}/policy-documents/${attachmentId}/`
      )
      invalidate()
      toast.success('Policy document deleted.')
    } catch (err) {
      toast.error(parseError(err))
    }
  }

  const handlePolicyDocUploaded = (policy: InsurancePolicyType, token: string | null) => {
    invalidate()
    toast.success('Policy document uploaded.')
    // Reading the PDF takes the LLM ~30s, so the change review lands separately.
    if (!token) return
    // One watcher per policy: a re-upload supersedes that policy's review server
    // side, but another policy's is still coming.
    setPendingReviews((current) => [
      ...current.filter((entry) => entry.policyId !== policy.id),
      { policyId: policy.id, token },
    ])
  }

  const handleQuoted = async (quoteId: string) => {
    await axios.post(`/api/v1/insure/organisations/${orgId}/quotes/${quoteId}/quoted/`)
    invalidate()
  }

  const handleRequestToBind = async (quoteId: string) => {
    try {
      await axios.post(`/api/v1/insure/organisations/${orgId}/quotes/${quoteId}/request-to-bind/`)
      invalidate()
    } catch (err) {
      toast.error(parseError(err))
    }
  }

  const handleBound = async (dateStart: string | undefined) => {
    if (!bindingQuote) return
    await axios.post(`/api/v1/insure/organisations/${orgId}/quotes/${bindingQuote.quoteId}/bound/`, {
      date_start: dateStart,
    })
    invalidate()
    setBindingQuote(null)
  }

  const handleGeneratePolicySummary = async (policyId: string) => {
    try {
      await axios.post(`/api/v1/insure/organisations/${orgId}/policies/${policyId}/generate-summary/`)
      toast.success('AI summary generation started. Refresh in a minute to see it.')
      invalidate()
    } catch {
      toast.error('Failed to start summary generation')
    }
  }

  const handleGenerateIndicationSummary = async (indicationId: string) => {
    try {
      await axios.post(`/api/v1/insure/organisations/${orgId}/indications/${indicationId}/generate-summary/`)
      toast.success('AI summary generation started. Refresh in a minute to see it.')
      invalidate()
    } catch {
      toast.error('Failed to start summary generation')
    }
  }

  const handleGenerateQuote = async (indicationId: string) => {
    try {
      await generateQuoteFromIndication(orgId, indicationId)
      toast.success('Quote generation started. Please wait several minutes...')
      invalidate()
    } catch (err) {
      toast.error(parseError(err))
    }
  }

  const handleToggleApplied = async (indicationId: string, applied: boolean) => {
    try {
      await setIndicationApplied(orgId, indicationId, applied)
      invalidate()
    } catch {
      toast.error('Failed to update indication')
    }
  }

  // Recommending one offer clears whatever else carried the flag — the backend owns that.
  const handleRecommendIndication = async (indicationId: string, recommended: boolean) => {
    try {
      await setIndicationRecommended(orgId, indicationId, recommended)
      invalidate()
    } catch (err) {
      toast.error(parseError(err))
    }
  }

  const handleDeleteIndication = async (indicationId: string) => {
    try {
      await deleteIndication(orgId, indicationId)
      invalidate()
    } catch {
      toast.error('Failed to delete indication')
    }
  }

  const startCreate = (kind: InsuranceOfferKind) => {
    setParsedPrefill(null)
    setCreatingKind(kind)
  }

  const startUpload = (kind: InsuranceOfferKind) => {
    setUploadKind(kind)
    setUploadOpen(true)
  }

  const handleParsedPolicy = (parsed: ParsedPolicyIndication, kind: InsuranceOfferKind) => {
    setParsedPrefill({
      vendor: parsed.vendor,
      vendorId: parsed.vendorId ?? '',
      carrierId: parsed.carrierId,
      unmatchedCarrierName: parsed.carrierId ? null : parsed.carrierName,
      coverages: parsed.coverages,
      oneTimeFees: parsed.oneTimeFees,
      sourceDocumentId: parsed.documentId,
      warnings: parsed.warnings,
    })
    setCreatingKind(kind)
    toast.success('Document parsed — review the prefilled fields and save.')
  }

  const handleDeleteQuote = async (quoteId: string) => {
    try {
      await deleteQuote(orgId, quoteId)
      invalidate()
    } catch {
      toast.error('Failed to delete quote')
    }
  }

  if (isLoading) {
    return <SkeletonRows className="mt-5" rows={6} rowClassName="h-16" />
  }

  if (!application) {
    return (
      <Card>
        <CardContent>
          <p className="text-muted-foreground text-sm">No application found for this organization.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <div className="flex flex-col gap-8">
        {policies.length > 0 && (
          <div>
            <h2 className="mb-2 text-xl font-bold">Policies</h2>
            <div className="flex flex-col gap-8">
              {policies.map((policy) => (
                <InsurancePolicy
                  key={policy.id}
                  policy={policy}
                  // Staff-only page (InternalLayout), so documents always show.
                  canViewDocuments
                  canEditCoiMode={!!user?.isCaiAdmin}
                  isStaff={user?.isStaff}
                  onEditPolicy={() => setPolicyDialog({ policy, prefill: null })}
                  onCancelPolicy={
                    user?.isCaiAdmin && CANCELABLE_STATUSES.has(policy.status) && !policy.isCanceled
                      ? () => setCancelingPolicy(policy)
                      : undefined
                  }
                  onUploadPolicyDoc={() => setUploadPolicyDoc(policy)}
                  onDeletePolicyDoc={(attachmentId) => handleDeletePolicyDoc(policy, attachmentId)}
                  onGenerateSummary={() => handleGeneratePolicySummary(policy.id)}
                  banner={user?.isCaiAdmin && <PostBindChecklist orgId={orgId} policyId={policy.id} />}
                />
              ))}
            </div>
          </div>
        )}

        <FailureAnalysisPanel applicationId={application.id} isCaiAdmin={user?.isCaiAdmin} />

        <div>
          <h2 className="mb-2 text-xl font-bold">Recent insurance applications</h2>
          {fillState?.needsReviewAttestation && (
            <div className="mb-4">
              <CustomerConfirmationWaiting key={orgId} orgId={orgId} state={fillState} />
            </div>
          )}
          {!showsQuotesPage(org, application) && (
            <p className="text-muted-foreground mb-4 text-sm">
              No prices yet — this customer sees the application until carriers come back.
            </p>
          )}
          {application.progressiveNotRun && (
            <div className="mb-4">
              <ProgressiveNotRunCard applicationId={application.id} orgId={orgId} />
            </div>
          )}
          {hasUnsentRocklakeLines && (
            <div className="mb-4">
              <RocklakeLinesCard
                lines={application.rocklakeManualLines}
                applicationId={application.id}
                orgId={orgId}
              />
            </div>
          )}
          {indications.length > 0 ? (
            <InsuranceIndications
              indications={indications}
              // Staff page, not the chat column — the quote action rows need the width.
              className="max-w-none"
              hideCheckbox
              // Staff-only page, so the card's staff affordances (carrier
              // portal link, submission screenshots, vendor id, Resubmit) show
              // here as they do on the Application tab.
              user={user}
              applicationId={application?.id}
              orgId={orgId}
              applicationEditedAt={application.dateLastEdited}
              // Quotes below already have their own bind actions.
              hideUserCta
              portalLinks={application?.portalLinks}
              renderDetailLinks={(indication) => {
                // Corgi's pages for the policy bound from one of this indication's quotes.
                const corgiPolicyLinks = policies
                  .filter((policy) => indication.quotes.some((quote) => quote.id === policy.quote))
                  .flatMap((policy) => policy.corgiPolicyLinks ?? [])

                return (
                  <>
                    <CorgiPolicyLinks links={corgiPolicyLinks} className="flex-col items-start gap-1" />
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto justify-start px-0 has-[>svg]:px-0"
                      href={adminHref(
                        `${ADMIN_BASE_URL}/insure/insuranceindication/${indication.id}/change/`
                      )}
                      target="_blank"
                    >
                      Django admin <ExternalLink className="size-3" />
                    </Button>
                  </>
                )
              }}
              renderDetailActions={(indication) => {
                const quotes = indication.quotes || []
                const hasPendingQuote = quotes.some((q) => q.status === InsuranceQuoteStatus.PENDING)

                return (
                  <div className="flex flex-col gap-2">
                    {indication.dateApplied && (
                      <span className="text-accent-green text-sm font-medium">Applied</span>
                    )}
                    {indication.status === InsuranceIndicationStatus.GENERATED && hasPendingQuote && (
                      <span className="text-warning-d1 text-sm">Quote generating...</span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setEditingIndication(indication)}
                    >
                      Edit Indication
                    </Button>
                    {indication.status === InsuranceIndicationStatus.GENERATED && (
                      <RecommendOfferButton
                        className="w-full"
                        recommended={indication.brokerRecommended}
                        recommendedByName={indication.brokerRecommendedByName}
                        onToggle={(recommended) => handleRecommendIndication(indication.id, recommended)}
                      />
                    )}
                    {indication.sourceDocumentUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => handleGenerateIndicationSummary(indication.id)}
                      >
                        {indication.aiSummary ? 'Regenerate AI summary' : 'Generate AI summary'}
                      </Button>
                    )}
                    {(indication.status === InsuranceIndicationStatus.GENERATED ||
                      indication.dateApplied) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => handleToggleApplied(indication.id, !indication.dateApplied)}
                      >
                        {indication.dateApplied ? 'Unapply' : 'Mark as applied'}
                      </Button>
                    )}
                    {indication.status === InsuranceIndicationStatus.GENERATED && quotes.length === 0 && (
                      <ConfirmationDialog
                        title="Generate Quote"
                        description="This will request a bindable quote from the carrier. Continue?"
                        confirmLabel="Generate"
                        onConfirm={() => handleGenerateQuote(indication.id)}
                      >
                        <Button size="sm" className="w-full">
                          Generate Quote
                        </Button>
                      </ConfirmationDialog>
                    )}
                    {quotes.length > 0 ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled
                        title="Delete its quotes first"
                      >
                        Delete
                      </Button>
                    ) : (
                      <ConfirmationDialog
                        title="Delete indication"
                        description="This permanently deletes the indication. This cannot be undone."
                        confirmLabel="Delete"
                        onConfirm={() => handleDeleteIndication(indication.id)}
                      >
                        <Button variant="outline" size="sm" className="w-full">
                          Delete
                        </Button>
                      </ConfirmationDialog>
                    )}
                  </div>
                )
              }}
              // Edit is mirrored here from the sidebar so it's reachable without "View details" first.
              renderIndicationChildren={(indication) => {
                const quotes = indication.quotes || []

                return (
                  <>
                    <div className="mt-4 flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingIndication(indication)}>
                        Edit
                      </Button>
                      {indication.status === InsuranceIndicationStatus.GENERATED && (
                        <RecommendOfferButton
                          recommended={indication.brokerRecommended}
                          recommendedByName={indication.brokerRecommendedByName}
                          onToggle={(recommended) => handleRecommendIndication(indication.id, recommended)}
                        />
                      )}
                    </div>
                    {/* Quotes list */}
                    {quotes.length > 0 && (
                      <div className="mt-4 border-t border-gray-200 pt-4">
                        <span className="text-general-foreground mb-3 block text-base font-semibold whitespace-nowrap">
                          Quotes ({quotes.length})
                        </span>
                        <div className="flex flex-col gap-3">
                          {quotes.map((quote) => {
                            // Camelized by toCamelCase on the way in — the
                            // backend writes manual_review_reasons.
                            const manualReviewReasons = (quote.metadata?.manualReviewReasons ?? {}) as Record<
                              string,
                              string[]
                            >
                            const isUnderReview =
                              quote.status === InsuranceQuoteStatus.PENDING &&
                              Object.keys(manualReviewReasons).length > 0

                            return (
                              <div key={quote.id} className="py-2">
                                <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2">
                                  <div className="flex min-w-0 flex-col gap-1">
                                    <div className="flex items-center gap-3">
                                      <span className="text-base font-medium">
                                        {quote.status === InsuranceQuoteStatus.PENDING && !isUnderReview && (
                                          <span className="text-warning-d1">Generating...</span>
                                        )}
                                        {isUnderReview && (
                                          <span className="text-warning-d1">Under Review</span>
                                        )}
                                        {quote.status === InsuranceQuoteStatus.QUOTED && (
                                          <span className="text-accent-green">Bindable</span>
                                        )}
                                        {quote.status === InsuranceQuoteStatus.REQUEST_TO_BIND && (
                                          <span className="text-accent-green">Requested to bind</span>
                                        )}
                                        {quote.status === InsuranceQuoteStatus.BOUND && (
                                          <span className="text-accent-green">Bound</span>
                                        )}
                                        {quote.status === InsuranceQuoteStatus.DECLINED && (
                                          <span className="text-error">Declined</span>
                                        )}
                                        {quote.status === InsuranceQuoteStatus.ERROR && (
                                          <span className="text-error">Error</span>
                                        )}
                                      </span>
                                      <span className="text-general-foreground text-base font-semibold">
                                        {formatCurrency(quote.premium)}
                                      </span>
                                    </div>
                                    {isUnderReview &&
                                      Object.entries(manualReviewReasons).map(([coverage, reasons]) => (
                                        <div key={coverage} className="text-muted-foreground text-xs">
                                          <span className="font-medium">
                                            {COVERAGE_LABELS[coverage] ?? coverage}:
                                          </span>{' '}
                                          {reasons.map((r) => MANUAL_REVIEW_LABELS[r] ?? r).join(', ')}
                                        </div>
                                      ))}
                                    {quote.declineDetail && (
                                      <QuoteDeclineBanner
                                        detail={quote.declineDetail}
                                        isError={quote.status === InsuranceQuoteStatus.ERROR}
                                      />
                                    )}
                                    {!!quote.metadata?.carrierAlreadyBound && (
                                      <div className="text-muted-foreground text-xs">
                                        Already bound at {quote.vendorName}
                                      </div>
                                    )}
                                    {quote.pricedStartDate && (
                                      <div className="text-muted-foreground text-xs">
                                        Starts {formatDate(quote.pricedStartDate)}
                                      </div>
                                    )}
                                    <QuoteTimestamps
                                      dateCreated={quote.dateCreated}
                                      dateUpdated={quote.dateUpdated}
                                    />
                                  </div>
                                  <div className="flex flex-wrap items-center gap-4">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        setEditingQuote({ indicationId: indication.id, quoteId: quote.id })
                                      }
                                    >
                                      Edit
                                    </Button>
                                    {quote.status === InsuranceQuoteStatus.BOUND ||
                                    quote.status === InsuranceQuoteStatus.REQUEST_TO_BIND ? (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        disabled
                                        title="Bound or pending-bind quotes can't be deleted"
                                      >
                                        Delete
                                      </Button>
                                    ) : (
                                      <ConfirmationDialog
                                        title="Delete quote"
                                        description="This permanently deletes the quote. This cannot be undone."
                                        confirmLabel="Delete"
                                        onConfirm={() => handleDeleteQuote(quote.id)}
                                      >
                                        <Button variant="outline" size="sm">
                                          Delete
                                        </Button>
                                      </ConfirmationDialog>
                                    )}
                                    {quote.status === InsuranceQuoteStatus.PENDING && (
                                      <ConfirmationDialog
                                        title="Quoted"
                                        description="Are you sure you want to mark this quote as ready to bind? This will let the business owners request to bind."
                                        confirmLabel="Quoted"
                                        onConfirm={() => handleQuoted(quote.id)}
                                      >
                                        <Button size="sm">Mark as Quoted</Button>
                                      </ConfirmationDialog>
                                    )}
                                    {canBind && quote.status === InsuranceQuoteStatus.QUOTED && (
                                      <RequestToBindButton
                                        blockedReason={bindBlockedReason}
                                        onConfirm={() => handleRequestToBind(quote.id)}
                                      />
                                    )}
                                    {quote.status === InsuranceQuoteStatus.REQUEST_TO_BIND && (
                                      <Button
                                        size="sm"
                                        onClick={() =>
                                          setBindingQuote({
                                            indicationId: indication.id,
                                            quoteId: quote.id,
                                            pricedStartDate: quote.pricedStartDate,
                                          })
                                        }
                                      >
                                        Bind
                                      </Button>
                                    )}
                                    <Button
                                      variant="link"
                                      size="sm"
                                      href={adminHref(
                                        `${ADMIN_BASE_URL}/insure/insurancequote/${quote.id}/change/`
                                      )}
                                      target="_blank"
                                    >
                                      Django admin <ExternalLink className="size-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )
              }}
            />
          ) : application.progressiveNotRun || hasUnsentRocklakeLines ? null : (
            <div className="text-muted-foreground text-md">No recent insurance application found.</div>
          )}
        </div>

        <PackageStatus orgId={orgId} application={application} />
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" onClick={() => startCreate(InsuranceOfferKind.INDICATION)}>
            <PackagePlus /> Create indication
          </Button>
          <Button size="sm" onClick={() => startCreate(InsuranceOfferKind.QUOTE)}>
            <PackagePlus /> Create quote
          </Button>
          <Button size="sm" onClick={() => setCreatingPackage(true)}>
            <PackagePlus /> Create package
          </Button>
          {application.package && (
            <ConfirmationDialog
              title="Delete package"
              description="The customer stops seeing this package on their quotes page. Continue?"
              confirmLabel="Delete"
              onConfirm={() => handleDeletePackage(application.package!.id)}
            >
              <Button size="sm" variant="outline">
                Delete package
              </Button>
            </ConfirmationDialog>
          )}
          <Button size="sm" variant="outline" onClick={() => startUpload(InsuranceOfferKind.INDICATION)}>
            <Upload /> Upload indication
          </Button>
          <Button size="sm" variant="outline" onClick={() => startUpload(InsuranceOfferKind.QUOTE)}>
            <Upload /> Upload quote
          </Button>
        </div>
      </div>

      <BindDialog
        // Keyed by quote, so each opening starts from that quote's priced date.
        key={bindingQuote?.quoteId}
        open={!!bindingQuote}
        pricedStartDate={bindingQuote?.pricedStartDate}
        onOpenChange={(open) => {
          if (!open) setBindingQuote(null)
        }}
        onConfirm={handleBound}
      />

      {editingIndication && (
        <IndicationFormDialog
          indication={editingIndication}
          orgId={orgId}
          open={!!editingIndication}
          onOpenChange={(open) => {
            if (!open) setEditingIndication(null)
          }}
        />
      )}

      {creatingKind && (
        <IndicationFormDialog
          orgId={orgId}
          kind={creatingKind}
          initialData={parsedPrefill ?? undefined}
          open
          onOpenChange={(open) => {
            if (open) return
            setCreatingKind(null)
            setParsedPrefill(null)
          }}
        />
      )}

      {creatingPackage && (
        <CreatePackageDialog orgId={orgId} application={application} open onOpenChange={setCreatingPackage} />
      )}

      <ParsePolicyDialog
        open={uploadOpen}
        kind={uploadKind}
        onOpenChange={setUploadOpen}
        orgId={orgId}
        onParsed={handleParsedPolicy}
      />

      {editingQuote &&
        (() => {
          const indication = indications.find((i) => i.id === editingQuote.indicationId)
          const quote = indication?.quotes?.find((q) => q.id === editingQuote.quoteId)
          if (!quote) return null
          return (
            <EditQuoteDialog
              quote={quote}
              orgId={orgId}
              open
              onOpenChange={(open) => {
                if (!open) setEditingQuote(null)
              }}
            />
          )
        })()}

      {policyDialog && (
        <PolicyFormDialog
          open
          onOpenChange={(open) => {
            if (!open) setPolicyDialog(null)
          }}
          orgId={orgId}
          policy={policyDialog.policy}
          prefill={policyDialog.prefill}
        />
      )}

      {cancelingPolicy && (
        <CancelPolicyDialog
          orgId={orgId}
          policy={cancelingPolicy}
          onOpenChange={(open) => {
            if (!open) setCancelingPolicy(null)
          }}
          onCanceled={invalidate}
        />
      )}

      {uploadPolicyDoc && (
        <UploadPolicyDocumentDialog
          open
          onOpenChange={(open) => {
            if (!open) setUploadPolicyDoc(null)
          }}
          orgId={orgId}
          policyId={uploadPolicyDoc.id}
          onUploaded={(token) => handlePolicyDocUploaded(uploadPolicyDoc, token)}
        />
      )}

      {pendingReviews.map(({ policyId, token }) => (
        <PolicyDocumentReviewWatcher
          key={token}
          orgId={orgId}
          policyId={policyId}
          token={token}
          onPreviewChanges={(prefill) => {
            const policy = policies.find((candidate) => candidate.id === policyId)
            if (policy) setPolicyDialog({ policy, prefill })
          }}
          onSettled={() => setPendingReviews((current) => current.filter((entry) => entry.token !== token))}
        />
      ))}
    </>
  )
}
